import { api } from "@/shared/api";
import { brandRows } from "@/shared/brand/persona";
import type { ApiDateTime } from "@/shared/lib/apiDateTime";

// Gap A(2026-06-10):rebuild 把 tenant billing 合并为单端点 `GET /tenant/billing/me` 并 enrich
// (displayName 查套餐 / daysRemaining 算 / totalUnits 取套餐 default_buckets grant),字段对齐下列类型。
// legacy 的 4 个 /me/{balance,policy,subscription,series} 子端点已退役,前端改消费本聚合视图。

export interface MySubscriptionView {
  planCode: string;
  displayName: string;
  periodDays: number;
  rolloverRatio: number;
  effectiveFrom: string;
  effectiveTo: string;
  status: "active" | "expired" | "suspended" | "refunded";
  daysRemaining: number;
  /**
   * 提前续费已付、尚未生效的下一期起始时刻(ISO;0926 修复波第五批,后端代理 N「提前续费不清零」)。
   * null / 缺省 = 没有待生效期。可选:老后端不带这个字段时前端照旧。
   */
  nextPeriodStartsAt?: string | null;
  /** 下一期额度(积分);null = 后端还没给出(字段在补),文案去掉括号那段。 */
  nextPeriodCredits?: number | null;
}

export interface MyBalanceView {
  bucketKey: string;
  generation: number;
  totalUnits: number;
  remainingUnits: number;
}

// 「我的套餐」模块卡:活跃产品模块(catalog active&&billable)⋈ 租户已开通能力。
// enabled=true 高亮(本套餐已含),false 灰显(未包含=升级引导)。唯一来源 = xb_module_catalog(经 /me/plan/modules)。
export interface PlanModuleCard {
  capability: string;
  displayName: string;
  meterKind: string; // SESSION | CREDIT | COUNT_CAP
  domain: string;
  enabled: boolean;
  /**
   * 0926 建站舱暂停开放:套餐里含、但该舱整体暂停开放(catalog coming_soon)。此时 enabled 恒为 false,
   * 卡片显示「即将上线」,不显示「已开通」。老后端不带这个字段 → undefined 当 false。
   */
  comingSoon?: boolean;
  /**
   * OEM 第二批:凭什么开 —— PLAN(套餐含)/ ADDON(增值舱)/ null(未拥有)。后端 A7(api 4d3832b1)起
   * `/me/plan/modules` 带这三个字段;仍按可选声明,老后端缺省 → 前端退回「已开通 / 未包含」旧口径,不猜。
   */
  ownedBy?: "PLAN" | "ADDON" | null;
  /** 已拥有但被服务商 / 平台暂停(此时 enabled=false)。 */
  paused?: boolean;
  /** 增值舱到期时刻(ownedBy=ADDON 才有值);生产应为 ISO 串,前端也认数组。 */
  addonEndAt?: ApiDateTime;
}

/** 本租户持有的增值舱(全部状态)。 */
export interface MyAddonHolding {
  capability: string;
  addonCode: string | null;
  displayName: string;
  /** ACTIVE / EXPIRED / RETIRED / REFUNDED */
  status: string;
  /** PARTNER_WALLET / DIRECT_PAID / GIFT */
  source: string;
  endAt: ApiDateTime;
}

/** 可加购清单的一项(只读;租户端不能自助下单)。 */
export interface MyAddonCatalogItem {
  addonCode: string;
  capability: string;
  displayName: string;
  description: string | null;
  /** 直营 = 一年牌价(micro);渠道租户 = null(不露价)。 */
  priceMicro: number | null;
  perSeatAmount: number | null;
  benefit: unknown;
  moduleStatus: string | null;
  purchasable: boolean;
  /** 不可加购的错误码名(ADDON_*);可加购 = null。 */
  reason: string | null;
  /** 不可加购 / 渠道租户的一句人话;直营可买 = null。 */
  reasonText: string | null;
}

/** GET /me/addons(OEM 第二批 A7):持有 + 可加购清单。不含积分 / 钱包 / 报价字段。 */
export interface MyAddons {
  planCode: string | null;
  currentPeriodEnd: ApiDateTime;
  holdings: MyAddonHolding[];
  catalog: MyAddonCatalogItem[];
}

// 近 N 天用量时序点(按天 × AI 模块)。数据源 = xb_billing_event(settle),capability 已由后端映射为中文名。
/** 「我的套餐」资源上限行(quota=null → 这一项没配额度;声纹带 parentCapability=ai_digital_human)。 */
export interface PlanResourceCard {
  capability: string;
  displayName: string;
  quota: number | null;
  parentCapability: string | null;
}

export interface MySeriesPoint {
  bucketDate: string; // "2026-07-01"
  moduleLabel: string; // 模块中文名(catalog),脱靶回退原 capability
  calls: number;
  /**
   * 该天该模块消耗的**积分**。后端 `TenantUsageSeriesPoint` 从 2026-08-23 起就随点返了,
   * 前端这一侧一直没声明也没用(2026-09-20 对账审计 E-03)—— 趋势图按 `calls` 画的后果是
   * 图例第一名成了 **0 积分**的「军师授权」(1545 次),而真花了 54 万积分的视频舱排第四。
   * ★只计 credit_pool 桶:客服/陪练的会话额度不是积分,后端那边恒 0,别拿来当钱看。
   */
  credits: number;
}

interface MyBillingView {
  balances: MyBalanceView[];
  subscription: MySubscriptionView | null;
}

const getMyBilling = () => api.get<MyBillingView>("/tenant/billing/me");

function qs(params: Record<string, string | number | undefined>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") {
      parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
    }
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export const myBillingService = {
  getMyBilling,
  // /tenant/billing/me 对无订阅(pending/老租户)可返 data:null,守 d?. 避免 queryFn 内裸 deref 抛。
  getSubscription: (): Promise<MySubscriptionView | null> =>
    getMyBilling().then((d) => d?.subscription ?? null),
  listBalance: (): Promise<MyBalanceView[]> => getMyBilling().then((d) => d?.balances ?? []),
  // 套餐模块清单(catalog 唯一来源 ⋈ 租户已开通)。TENANT_ADMIN「我的套餐」页消费。
  // 1004 O5:目录名过 brandRows(直营人设名 → 当前品牌人设),下同。
  listPlanModules: (): Promise<PlanModuleCard[]> =>
    api.get<PlanModuleCard[]>("/me/plan/modules").then((rows) => brandRows(rows, "displayName")),
  // OEM 第二批 A7:增值舱持有 + 可加购清单(只读)。平台超管本人无租户上下文 → 403。
  getMyAddons: (): Promise<MyAddons> =>
    api.get<MyAddons>("/me/addons").then((d) =>
      d
        ? {
            ...d,
            holdings: brandRows(d.holdings, "displayName"),
            catalog: brandRows(d.catalog, "displayName", "description", "reasonText"),
          }
        : d
    ),
  // 资源上限(席位/军师授权/声纹):catalog COUNT_CAP ⋈ entitlement.quota(null=没配额度)。
  listPlanResources: (): Promise<PlanResourceCard[]> =>
    api
      .get<PlanResourceCard[]>("/me/plan/resources")
      .then((rows) => brandRows(rows, "displayName")),
  // 近 N 天用量趋势(按天 × AI 模块),后端聚合 xb_billing_event(settle)。空数据 → 图表显示空态。
  getSeries: (from: string, to: string): Promise<MySeriesPoint[]> =>
    api
      .get<MySeriesPoint[]>(`/tenant/billing/me/usage-series${qs({ from, to })}`)
      .then((rows) => brandRows(rows, "moduleLabel")),
};
